'use client';
import { useState, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  Plane, ArrowRight, Phone, X, CheckCircle, ArrowLeft,
  Users, Briefcase, Calendar, Search, Clock, Globe,
  Shield, Star, ChevronRight, MapPin
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
}
function extractCity(str) {
  if (!str) return '';
  return str.replace(/\s*\([A-Z]{3}\)/, '').trim() || str;
}
function extractIATA(str) {
  if (!str) return '';
  const m = str.match(/\(([A-Z]{3})\)/);
  return m ? m[1] : '';
}

// ── Contact / Callback Modal ─────────────────────────────────────────
function ContactModal({ searchData, onClose }) {
  const [form, setForm]       = useState({ name: '', phone: '', email: '' });
  const [submitting, setSub]  = useState(false);
  const [success, setSuccess] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSub(true);
    try {
      await fetch('/api/flight-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...searchData, ...form }),
      });
      setSuccess(true);
    } catch {}
    setSub(false);
  }

  const fromCity = extractCity(searchData.from);
  const toCity   = extractCity(searchData.to);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {!success ? (
          <>
            <div className="relative bg-gradient-to-r from-sky-600 to-sky-400 p-6 text-white">
              <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white"><X size={20} /></button>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Phone size={20} /></div>
                <div>
                  <h2 className="font-bold text-xl">Consiga o Melhor Preço de Voo</h2>
                  <p className="text-sky-100 text-sm">Nossos especialistas encontram as tarifas mais baixas para você</p>
                </div>
              </div>
              <div className="bg-white/10 rounded-2xl p-3 flex items-center gap-3">
                <Phone size={18} className="text-sky-200" />
                <div>
                  <div className="font-bold text-xl tracking-wide">+34-868-26-9023</div>
                  <div className="text-sky-200 text-xs">Ligação gratuita · Suporte 24/7</div>
                </div>
              </div>
            </div>

            {/* Search summary */}
            <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Sua Pesquisa</p>
              <div className="flex items-center gap-2 flex-wrap">
                {searchData.from && (
                  <span className="bg-sky-50 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1">
                    <MapPin size={11} /> {searchData.from}
                  </span>
                )}
                {searchData.to && (
                  <>
                    <ArrowRight size={12} className="text-slate-400" />
                    <span className="bg-sky-50 text-sky-700 text-xs px-2.5 py-1.5 rounded-lg font-semibold flex items-center gap-1">
                      <MapPin size={11} /> {searchData.to}
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {searchData.departure && (
                  <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                    <Calendar size={10} /> {fmtDate(searchData.departure)}
                  </span>
                )}
                {searchData.returnDate && (
                  <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-lg">
                    Retorno: {fmtDate(searchData.returnDate)}
                  </span>
                )}
                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                  <Users size={10} /> {searchData.passengers}
                </span>
                <span className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                  <Briefcase size={10} /> {searchData.cabinClass}
                </span>
              </div>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <p className="text-slate-600 text-sm">Deixe seus dados e ligaremos para você em até <strong>15 minutos</strong> com as melhores tarifas disponíveis.</p>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Nome Completo *</label>
                <input required
                  className="w-full border border-slate-200 px-4 py-3 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Número de Telefone *</label>
                <input required type="tel"
                  className="w-full border border-slate-200 px-4 py-3 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  placeholder="+1 555 000 0000"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Endereço de E-mail</label>
                <input type="email"
                  className="w-full border border-slate-200 px-4 py-3 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  placeholder="voce@email.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
                <Phone size={17} /> {submitting ? 'Enviando…' : 'Solicitar Ligação'}
              </button>
            </form>
          </>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={36} className="text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Solicitação Recebida!</h3>
            <p className="text-slate-500 mb-2">Nosso especialista em viagens ligará para você em até <strong>15 minutos</strong>.</p>
            <p className="text-sky-500 font-bold text-lg mb-6">+34-868-26-9023</p>
            <button onClick={onClose} className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors">Concluído</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Why call us cards ─────────────────────────────────────────────────
const WHY_CALL = [
  { icon: Shield,  title: 'Melhor Preço Garantido', desc: 'Comparamos mais de 500 companhias aéreas para conseguir a tarifa mais baixa — incluindo ofertas não publicadas.' },
  { icon: Phone,   title: 'Assistência Especializada',      desc: 'Agentes de viagem dedicados cuidam da sua reserva, seleção de assento e solicitações especiais.' },
  { icon: Clock,   title: 'Suporte 24/7',            desc: 'Ajuda o dia todo, da pesquisa ao pouso, incluindo alterações de última hora.' },
  { icon: Star,    title: 'Confiado por Milhões',     desc: 'Mais de 2 milhões de viajantes reservam conosco todos os anos em mais de 120 países.' },
];

// ── Main ─────────────────────────────────────────────────────────────
function FlightsContent() {
  const params = useSearchParams();
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  const searchData = {
    from:       params.get('from')       || '',
    to:         params.get('to')         || '',
    departure:  params.get('departure')  || '',
    returnDate: params.get('return')     || '',
    passengers: params.get('passengers') || '1 Adult',
    cabinClass: params.get('class')      || 'Economy',
    tripType:   params.get('trip')       || 'Só Ida',
  };

  const fromCity = extractCity(searchData.from);
  const toCity   = extractCity(searchData.to);
  const fromCode = extractIATA(searchData.from);
  const toCode   = extractIATA(searchData.to);

  const hasSearch = !!(searchData.from && searchData.to);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-slate-500" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-sky-600 transition-colors">Início</Link>
          <ChevronRight size={11} className="text-slate-400" />
          <span className="text-sky-600 font-medium">Voos</span>
        </nav>
      </div>

      {/* ── Sticky search summary bar ── */}
      <div className="bg-white border-b border-slate-100 shadow-sm sticky top-16 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => router.back()} className="text-slate-400 hover:text-slate-800 transition-colors flex-shrink-0">
            <ArrowLeft size={20} />
          </button>

          {hasSearch ? (
            <div className="flex items-center gap-2 font-bold text-slate-900 text-sm">
              <span>{fromCity || searchData.from}</span>
              {fromCode && <span className="text-slate-500 font-normal text-xs">({fromCode})</span>}
              <ArrowRight size={14} className="text-sky-500 flex-shrink-0" />
              <span>{toCity || searchData.to}</span>
              {toCode && <span className="text-slate-500 font-normal text-xs">({toCode})</span>}
            </div>
          ) : (
            <span className="text-slate-500 text-sm">Nova Pesquisa</span>
          )}

          {searchData.departure && (
            <span className="text-slate-500 text-xs bg-slate-100 px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0">
              <Calendar size={11} /> {fmtDate(searchData.departure)}
              {searchData.returnDate && <> · Retorno: {fmtDate(searchData.returnDate)}</>}
            </span>
          )}
          <span className="text-slate-500 text-xs bg-slate-100 px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0">
            <Users size={11} /> {searchData.passengers}
          </span>
          <span className="text-slate-500 text-xs bg-slate-100 px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0">
            <Briefcase size={11} /> {searchData.cabinClass}
          </span>

          <div className="ml-auto flex-shrink-0">
            <button
              onClick={() => setShowModal(true)}
              className="bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 transition-colors"
            >
              <Phone size={13} /> Melhor Preço
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">

        {/* ── Hero CTA block ── */}
        <div className="bg-gradient-to-br from-sky-600 via-sky-500 to-sky-400 rounded-3xl p-8 md:p-10 text-white mb-10 shadow-xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              {hasSearch ? (
                <>
                  <p className="text-sky-100 text-sm font-semibold uppercase tracking-wider mb-2">Voos encontrados para sua rota</p>
                  <h1 className="text-3xl md:text-4xl font-bold mb-1 leading-tight">
                    {fromCity || searchData.from}
                    <span className="mx-3 text-sky-200">→</span>
                    {toCity || searchData.to}
                  </h1>
                  <div className="flex flex-wrap gap-2 mt-3 text-sm text-sky-100">
                    {searchData.departure && (
                      <span className="flex items-center gap-1 bg-white/10 rounded-lg px-3 py-1">
                        <Calendar size={13} /> {fmtDate(searchData.departure)}
                        {searchData.returnDate && ` – ${fmtDate(searchData.returnDate)}`}
                      </span>
                    )}
                    <span className="flex items-center gap-1 bg-white/10 rounded-lg px-3 py-1">
                      <Users size={13} /> {searchData.passengers}
                    </span>
                    <span className="flex items-center gap-1 bg-white/10 rounded-lg px-3 py-1">
                      <Briefcase size={13} /> {searchData.cabinClass}
                    </span>
                    <span className="flex items-center gap-1 bg-white/10 rounded-lg px-3 py-1">
                      {searchData.tripType}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sky-100 text-sm font-semibold uppercase tracking-wider mb-2">Encontre seu voo perfeito</p>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">Pesquise Qualquer Rota no Mundo</h1>
                  <p className="text-sky-100">Use o formulário de pesquisa na página inicial para encontrar voos para sua rota.</p>
                </>
              )}
            </div>

            <div className="flex flex-col gap-3 flex-shrink-0 w-full md:w-auto">
              <button
                onClick={() => setShowModal(true)}
                className="bg-white text-sky-600 font-bold px-8 py-4 rounded-2xl hover:bg-sky-50 transition-colors flex items-center justify-center gap-2 shadow-lg text-base"
              >
                <Phone size={18} /> Ligue para as Melhores Tarifas
              </button>
              <p className="text-sky-100 text-xs text-center">
                📞 +34-868-26-9023 · Retorno de ligação gratuito em 15 min
              </p>
            </div>
          </div>
        </div>

        {/* ── How it works ── */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Como conseguimos a melhor tarifa para você</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { step: '01', icon: Search,   title: 'Você Pesquisa',         desc: 'Informe sua rota, datas e dados dos passageiros.' },
              { step: '02', icon: Phone,    title: 'Nós Ligamos',   desc: 'Nosso especialista entra em contato em até 15 minutos.' },
              { step: '03', icon: Plane,    title: 'Reserve e Voe',         desc: 'Confirme a melhor tarifa e nós cuidamos do resto.' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center">
                    <Icon size={18} className="text-white" />
                  </div>
                </div>
                <div>
                  <span className="text-xs font-bold text-sky-500 uppercase tracking-wider">Etapa {step}</span>
                  <h3 className="font-bold text-slate-900 mt-0.5 mb-1">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Why call us ── */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Por que reservar com nossos especialistas?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHY_CALL.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex gap-4">
                <div className="w-11 h-11 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-sky-500" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Airlines we work with ── */}
        <div className="mb-10 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Mais de 500 companhias aéreas, uma única ligação</h2>
          <p className="text-slate-500 text-sm mb-5">Pesquisamos entre as principais companhias aéreas, incluindo as de baixo custo, para encontrar a melhor opção para você.</p>
          <div className="flex flex-wrap gap-2">
            {[
              'IndiGo', 'Air India', 'Emirates', 'Qatar Airways', 'Lufthansa',
              'British Airways', 'Singapore Airlines', 'Air France', 'KLM',
              'Turkish Airlines', 'Etihad', 'United Airlines', 'Delta', 'American Airlines',
              'SpiceJet', 'Vistara', 'Air Asia', 'Cathay Pacific', 'Japan Airlines',
            ].map(a => (
              <span key={a} className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-xl">
                {a}
              </span>
            ))}
            <span className="bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold px-3 py-1.5 rounded-xl">
              + 480 outras
            </span>
          </div>
        </div>

        {/* ── Popular routes ── */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Rotas populares</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { from: 'New York (JFK)',    to: 'London (LHR)',     tag: 'Mais Pesquisada' },
              { from: 'Dubai (DXB)',       to: 'Mumbai (BOM)',     tag: 'Popular' },
              { from: 'London (LHR)',      to: 'Paris (CDG)',      tag: 'Econômica' },
              { from: 'Los Angeles (LAX)', to: 'Tokyo (NRT)',      tag: 'Longa Distância' },
              { from: 'Delhi (DEL)',       to: 'Singapore (SIN)',  tag: 'Mais Escolhida' },
              { from: 'Sydney (SYD)',      to: 'Auckland (AKL)',   tag: 'Curta Distância' },
            ].map(({ from, to, tag }) => (
              <button
                key={`${from}-${to}`}
                onClick={() => setShowModal(true)}
                className="group bg-white rounded-2xl p-4 border border-slate-100 hover:border-sky-300 hover:shadow-md transition-all text-left flex items-center gap-3"
              >
                <div className="w-9 h-9 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-sky-100 transition-colors">
                  <Plane size={16} className="text-sky-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 truncate">
                    <span className="truncate">{extractCity(from)}</span>
                    <ArrowRight size={12} className="text-sky-400 flex-shrink-0" />
                    <span className="truncate">{extractCity(to)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{from} → {to}</p>
                </div>
                <span className="text-xs font-semibold bg-sky-50 text-sky-600 px-2 py-0.5 rounded-lg flex-shrink-0">{tag}</span>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-sky-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className="bg-gradient-to-r from-sky-600 to-sky-400 rounded-2xl p-8 text-center text-white shadow-lg">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Phone size={26} className="text-white" />
          </div>
          <p className="font-bold text-2xl mb-2">Pronto para reservar?</p>
          <p className="text-sky-100 mb-6">
            {hasSearch
              ? `Nossos especialistas estão prontos para encontrar as melhores tarifas para ${fromCity || 'sua partida'} → ${toCity || 'seu destino'}.`
              : 'Nossos especialistas estão prontos para encontrar as tarifas mais baixas para qualquer rota no mundo.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowModal(true)}
              className="bg-white text-sky-600 font-bold px-8 py-3.5 rounded-xl hover:bg-sky-50 transition-colors inline-flex items-center justify-center gap-2 shadow"
            >
              <Phone size={18} /> Solicitar Retorno Gratuito
            </button>
            <a
              href="tel:+34868269023"
              className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold px-8 py-3.5 rounded-xl transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} /> Ligar +34-868-26-9023
            </a>
          </div>
        </div>
      </div>

      <Footer />

      {showModal && (
        <ContactModal searchData={searchData} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

export default function FlightsClient() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-500" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-sky-600 transition-colors">Início</Link>
            <ChevronRight size={11} className="text-slate-400" />
            <span className="text-sky-600 font-medium">Voos</span>
          </nav>
        </div>
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3 text-center px-4">
            <h1 className="sr-only">Pesquise Qualquer Rota no Mundo | Voosworld</h1>
            <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
            <div className="text-slate-500 text-sm">Carregando…</div>
          </div>
        </div>
      </div>
    }>
      <FlightsContent />
    </Suspense>
  );
}
